import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { POSTransaction } from "../stores/types";
import { useUserContext } from "../../context/UserContext.tsx";

interface IArgs {
  cameraId: string;
  currentTimestamp: number | null;
  playbackOn: boolean;
}

const OFFSET_MS = 10_000; // 10 sec lookahead
// const MAX_RETRIES = 3;
// const RETRY_DELAY = 1000; 
export const CAMERA_TRANSACTIONS_CHUNK = 30;


/**
 * Reduce transactions → latest Tx per POS before currentTimestamp
 */
export const getPOSOverlayTransactions = ({
  transactions,
  currentTimestamp,
}: {
  transactions?: POSTransaction[];
  currentTimestamp: number | null;
}): Record<string, POSTransaction> | undefined => {
  if (!transactions || !transactions.length || !currentTimestamp) return;


  return transactions.reduce((acc, item) => {
    const { posId } = item;
    const itemTimestamp = new Date(item.startedAt).getTime();

    if (itemTimestamp > currentTimestamp) return acc;

    if (!acc[posId]) {
      acc[posId] = item;
      return acc;
    }

    const lastTs = new Date(acc[posId].startedAt).getTime();
    if (itemTimestamp > lastTs) {
      acc[posId] = item;
    }
    return acc;
  }, {} as Record<string, POSTransaction>);
};

export const usePlaybackTransactions = ({
  cameraId,
  currentTimestamp,
  playbackOn,
}: IArgs) => {
  const [playbackTransactions, setPlaybackTransactions] = useState<
    POSTransaction[]
  >([]);
  const [lastLoadedEndTime, setLastLoadedEndTime] = useState<number | null>(null);
  const { posCamera, activeRemoteUrl } = useUserContext();

  const loadingRef = useRef(false); // replaces loading state

  // ✔ safe flag instead of early return
  const isPosMatch = cameraId && posCamera === cameraId;

  //const remoteUrl = "http://arthurst7-11.myliveeye.com:8000";

  const currentPOSTransactions = useMemo(() => {
    if (!isPosMatch) return {};
    return getPOSOverlayTransactions({
      transactions: playbackTransactions,
      currentTimestamp,
    });
  }, [playbackTransactions, currentTimestamp, isPosMatch]);

  // reset if user jumps far away
  useEffect(() => {
    if (!isPosMatch) {
      setPlaybackTransactions([]);                // <-- reset when switching cameras
      return;
    }
    if (!currentTimestamp || !playbackTransactions.length) return;

    const firstTxTime = new Date(playbackTransactions[0].startedAt).getTime();
    const lastTxTime = new Date(playbackTransactions.at(-1)!.startedAt).getTime();

    const isOutsideRange =
      currentTimestamp < firstTxTime ||
      currentTimestamp > lastTxTime + OFFSET_MS;

    if (isOutsideRange) {
      // console.log("[PlaybackTx] Resetting transactions due to far seek");
      setPlaybackTransactions([]);
    }
  }, [currentTimestamp, isPosMatch]);

  // Update lastLoadedEndTime whenever new transactions are loaded
  useEffect(() => {
    if (!isPosMatch) return;
    if (playbackTransactions.length > 0) {
      const lastTx = playbackTransactions.at(-1);
      if (lastTx) {
        setLastLoadedEndTime(new Date(lastTx.startedAt).getTime());
      }
    }
  }, [playbackTransactions, isPosMatch]);

  const needLoadTransactions = useMemo(() => {
    if (!isPosMatch) return false;
    if (!currentTimestamp) return false;
    if (!playbackTransactions.length) return true;

    // Only load if currentTimestamp is within OFFSET_MS of lastLoadedEndTime
    if (lastLoadedEndTime !== null) {
      return currentTimestamp > lastLoadedEndTime - OFFSET_MS;
    }
    return false;
  }, [playbackTransactions, currentTimestamp, lastLoadedEndTime]);

  useEffect(() => {
    if (!isPosMatch) return;
    if (!needLoadTransactions || !playbackOn || !currentTimestamp) return;
    if (loadingRef.current) return;

    loadingRef.current = true;
    axios.get(`${activeRemoteUrl}/v1/pos/transactions/camera/${cameraId}?startTime=${currentTimestamp}&limit=${CAMERA_TRANSACTIONS_CHUNK}`)
      .then((res) => {
        const tx = res?.data?.success?.transactions ?? [];
        if (tx.length > 0) {
          setPlaybackTransactions((prev) => [...prev, ...tx]);
        } 
        // If empty, do not keep calling API until timestamp moves forward
        // Optionally, you could set a flag here to prevent further calls until timestamp advances
      })
      .catch((err) => {
        console.error("[PlaybackTx] fetch failed", err);
      })
      .finally(() => {
        loadingRef.current = false;
      });
  }, [needLoadTransactions, playbackOn, cameraId, isPosMatch]);

  return { currentPOSTransactions, playbackTransactions };
};  
